/**
 * Group / replica mega-menu header — JSON on the builder element and the
 * published runtime payload (`global_elements.content` page-builder tree).
 *
 * Modeled on the dense “group header” replica (utility strip, brand switcher,
 * modal search, layout-variant mega panels, mobile drawer). Stored on existing
 * JSON columns; no new Prisma fields.
 *
 * CTA / footer-link shapes match `MegaMenuHeaderActionLink` /
 * `MegaMenuHeaderFooterLink` structurally. Only `MenuRootNode` is type-imported
 * from `./index` (erased; no runtime cycle).
 */

import type { MenuRootNode } from "./index";

/** Icon placement beside a nav label (replica chrome). */
export type NavIconPosition = "left" | "right";

/** Builder / runtime element discriminant for this header variant. */
export type GroupMegaMenuHeaderElementType = "group_mega_menu_header";

/**
 * Top-bar cluster layout (logo, primary nav, actions).
 * Replica default is logo left, nav centre-left, CTAs right.
 */
export type GroupHeaderBarLayout =
	| "logo_nav_actions"
	| "logo_actions_nav"
	| "centered_nav"
	| "stacked";

/**
 * Mega panel chrome for one top-level tab.
 * `dense_columns` = company; `_featured` = services; `product_cards_groups` =
 * software; `_deadlines` = resources; `left_tabs` = tabular left rail.
 */
export type GroupHeaderMegaPanelLayout =
	| "dense_columns"
	| "dense_columns_featured"
	| "product_cards_groups"
	| "dense_columns_deadlines"
	| "left_tabs";

/** Spec / screenshot aliases mapped onto `GroupHeaderMegaPanelLayout`. */
export const GROUP_HEADER_LAYOUT_ALIASES: Record<
	string,
	GroupHeaderMegaPanelLayout
> = {
	dense_columns: "dense_columns",
	columns_only: "dense_columns",
	company: "dense_columns",
	dense_columns_featured: "dense_columns_featured",
	services: "dense_columns_featured",
	dense: "dense_columns_featured",
	product_cards_groups: "product_cards_groups",
	product_cards: "product_cards_groups",
	software: "product_cards_groups",
	dense_columns_deadlines: "dense_columns_deadlines",
	columns_plus_deadlines: "dense_columns_deadlines",
	resources: "dense_columns_deadlines",
	left_tabs: "left_tabs",
	"tabular-left": "left_tabs",
	tabular_left: "left_tabs",
	"left-tabs": "left_tabs",
	tabular: "left_tabs",
};

export function parseGroupHeaderMegaPanelLayout(
	value: unknown
): GroupHeaderMegaPanelLayout {
	if (typeof value !== "string") {
		return "dense_columns_featured";
	}
	return GROUP_HEADER_LAYOUT_ALIASES[value] ?? "dense_columns_featured";
}

/**
 * How subcategory columns are sourced.
 * `menu_groups` uses this header’s bound menu mega groups;
 * `linked_menu` binds another Theme Builder menu as the subcategory tree;
 * `inline_groups` authors dense groups on the panel JSON.
 */
export type GroupHeaderSubcategoryMode =
	| "menu_groups"
	| "linked_menu"
	| "inline_groups";

/** Compact = shrink bar + collapse utility strip after scroll (replica). */
export type GroupHeaderStickyMode = "always" | "smart" | "compact";

export type GroupHeaderSearchStyle = "icon" | "bar" | "modal";

export type GroupHeaderMobileStyle = "hamburger" | "drawer" | "bottom_sheet";

export type GroupHeaderDrawerSide = "left" | "right";

export type GroupHeaderDrawerBodyLayout =
	| "list"
	| "accordion"
	| "accordion_columns";

export type GroupHeaderLinkBadgeTone = "accent" | "soft";

/** Same fields as `MegaMenuHeaderActionLink`. */
export interface GroupHeaderCtaLink {
	enabled: boolean;
	href: string;
	label: string;
}

/** Same fields as `MegaMenuHeaderFooterLink`. */
export interface GroupHeaderFooterNavLink {
	href: string;
	id: string;
	label: string;
}

/** Per-link pill (e.g. “New”, “Refunds”) keyed by menu item id. */
export interface GroupHeaderLinkBadge {
	label: string;
	tone: GroupHeaderLinkBadgeTone;
}

/** “All SDLT services” row at the end of a dense group, keyed by mega_group id. */
export interface GroupHeaderGroupMoreLink {
	href: string;
	label: string;
}

export interface GroupHeaderDenseLink {
	badge?: string;
	badgeTone?: GroupHeaderLinkBadgeTone;
	href: string;
	id: string;
	label: string;
	openInNewTab?: boolean;
}

export interface GroupHeaderDenseGroup {
	href?: string;
	id: string;
	items: GroupHeaderDenseLink[];
	label: string;
	more?: GroupHeaderGroupMoreLink | null;
}

export interface GroupHeaderRegion {
	code: string;
	langs: string[];
	name: string;
	timezoneNote: string;
}

export interface GroupHeaderRegionSwitcher {
	currencyLabel: string;
	defaultLangIndex: number;
	defaultRegionCode: string;
	enabled: boolean;
	regions: GroupHeaderRegion[];
}

export interface GroupHeaderTicker {
	enabled: boolean;
	href: string;
	linkLabel: string;
	text: string;
}

export interface GroupHeaderUtilityLink {
	href: string;
	label: string;
}

export interface GroupHeaderUtility {
	enabled: boolean;
	links: GroupHeaderUtilityLink[];
	regionSwitcher: GroupHeaderRegionSwitcher;
	ticker: GroupHeaderTicker;
}

export interface GroupHeaderBrand {
	accent: string;
	accentDeep: string;
	accentSoft: string;
	ctaHref?: string;
	ctaLabel?: string;
	href: string;
	id: string;
	logoMediaId?: string;
	logoUrl?: string;
	mono: string;
	name: string;
	serif?: boolean;
	tag: string;
}

export interface GroupHeaderLogoSwitcher {
	autoplayMs: number;
	brands: GroupHeaderBrand[];
	enabled: boolean;
	pauseOnHover: boolean;
	showDots: boolean;
	showNavButtons: boolean;
	stageHeightPx: number;
	stageWidthPx: number;
}

export interface GroupHeaderSearch {
	emptyMessage: string;
	enabled: boolean;
	maxResults: number;
	placeholder: string;
	shortcutEnabled: boolean;
	style: GroupHeaderSearchStyle;
}

export interface GroupHeaderActions {
	/** When true, primary CTA label/href follow the active brand. */
	brandOverridesCta: boolean;
	primaryCta: GroupHeaderCtaLink;
	search: GroupHeaderSearch;
	secondaryCta: GroupHeaderCtaLink;
}

export interface GroupHeaderBehavior {
	barHeightPx: number;
	/** Drop shadow on the compact/stuck bar (`--ghh-hdr-shadow`). */
	barShadow: boolean;
	closeDelayMs: number;
	compactBarHeightPx: number;
	compactScrollY: number;
	glassBackground: boolean;
	/** Backdrop blur when `glassBackground` is on. Replica CSS default 18. */
	glassBlurPx: number;
	/** Default side for top-level nav icons when a nav icon has no `iconPosition`. */
	navIconPosition: NavIconPosition;
	openDelayMs: number;
	showNavCounts: boolean;
	sticky: boolean;
	stickyMode: GroupHeaderStickyMode;
	utilHeightPx: number;
	zIndex: number;
}

export interface GroupHeaderBreakpoints {
	/** Hide primary CTA. Replica default 560. */
	compactCtaHidePx: number;
	/** Switch to mobile drawer / burger. Replica default 1024. */
	drawerPx: number;
	/** Hide mega asides (featured / deadlines). Replica default 1240. */
	megaAsideHidePx: number;
}

export interface GroupHeaderTypography {
	fontFamily: string;
	groupTitleFontSizePx: number;
	linkFontSizePx: number;
	navFontSizePx: number;
	navFontWeight: number;
	utilFontSizePx: number;
}

export interface GroupHeaderDesignTokens {
	/**
	 * Empty / theme-default / legacy replica hex → inherit Theme Manager
	 * (`GROUP_HEADER_THEME_TOKEN_DEFAULTS`). A filled picker is an override.
	 */
	electric: string;
	/** Ghost (secondary) CTA label — not locked to Royal fill. */
	ghostCtaText: string;
	headerBackground: string;
	ice: string;
	/** Main header nav labels only (not the utility strip). */
	ink: string;
	line: string;
	lineSubtle: string;
	maxWidthPx: number;
	megaBackground: string;
	/** Mega panel link row fill on hover (empty → ice / brand accent soft). */
	megaLinkHoverBackground: string;
	/** Mega panel link label on hover (empty → royal / brand accent deep). */
	megaLinkHoverText: string;
	mute: string;
	/** Top-level nav hover / open tab row fill (empty → transparent). */
	navItemHoverBackground: string;
	/** Top-level nav hover / open tab text (empty → royal / brand accent deep). */
	navItemHoverText: string;
	navy: string;
	/** Primary CTA label — not locked to Electric fill. */
	primaryCtaText: string;
	radiusLg: string;
	radiusMd: string;
	/** Selected region / language row fill (empty → ice / brand accent soft). */
	regionItemSelectedBackground: string;
	/** Selected region / language row text (empty → royal / brand accent deep). */
	regionItemSelectedText: string;
	/** Region / language list row label (empty → ink). */
	regionItemText: string;
	/** Region / language popover panel fill (empty → mega panel background). */
	regionPopBackground: string;
	/** Region / language section headings (empty → readable fg/bg mix, not muted surface). */
	regionPopHeading: string;
	royal: string;
	sky: string;
	utilBackground: string;
	/** Utility strip links (ticker CTA + util nav). */
	utilLink: string;
	/** Utility strip ticker / region body copy. */
	utilText: string;
	wrapPaddingPx: number;
}

/**
 * Public + canvas inherit map (`applyGroupHeaderCssVars` / CSS `--ghh-*`).
 * Bare `var(--tt-*)` so ColorPicker theme swatches match; CSS files add hex
 * fallbacks for unthemed trees.
 */
export const GROUP_HEADER_THEME_TOKEN_DEFAULTS = {
	navy: "var(--tt-color-fg)",
	royal: "var(--tt-color-secondary)",
	electric: "var(--tt-color-primary)",
	sky: "var(--tt-color-accent)",
	ice: "color-mix(in srgb, var(--tt-color-primary) 14%, var(--tt-color-bg))",
	ink: "var(--tt-color-fg)",
	mute: "var(--tt-color-muted)",
	line: "color-mix(in srgb, var(--tt-color-primary) 22%, var(--tt-color-bg))",
	lineSubtle:
		"color-mix(in srgb, var(--tt-color-primary) 10%, var(--tt-color-bg))",
	headerBackground: "color-mix(in srgb, var(--tt-color-bg) 88%, transparent)",
	utilBackground: "var(--tt-color-fg)",
	utilText: "color-mix(in srgb, var(--tt-color-bg) 82%, var(--tt-color-fg))",
	utilLink: "var(--tt-color-bg)",
	primaryCtaText: "var(--tt-color-primary-foreground)",
	ghostCtaText: "var(--tt-color-secondary)",
	megaBackground: "var(--tt-color-bg)",
	regionPopBackground: "var(--tt-color-bg)",
	regionPopHeading:
		"color-mix(in srgb, var(--tt-color-fg) 62%, var(--tt-color-bg))",
	regionItemText: "var(--tt-color-fg)",
	regionItemSelectedBackground:
		"color-mix(in srgb, var(--tt-color-primary) 14%, var(--tt-color-bg))",
	regionItemSelectedText: "var(--tt-color-secondary)",
	navItemHoverText: "var(--tt-color-secondary)",
	navItemHoverBackground: "transparent",
	megaLinkHoverText: "var(--tt-color-secondary)",
	megaLinkHoverBackground:
		"color-mix(in srgb, var(--tt-color-primary) 14%, var(--tt-color-bg))",
} as const satisfies Record<
	Exclude<
		keyof GroupHeaderDesignTokens,
		"maxWidthPx" | "wrapPaddingPx" | "radiusMd" | "radiusLg"
	>,
	string
>;

/** Replica HTML palette — treat as inherit so unsaved hex defaults follow theme. */
export const LEGACY_GROUP_HEADER_HEX = {
	navy: "#071633",
	royal: "#0F3D91",
	electric: "#2E6BF6",
	sky: "#9DC1FF",
	ice: "#EDF3FF",
	ink: "#0B1D3F",
	mute: "#5B6B8C",
	line: "#D9E4F7",
	lineSubtle: "#EAF0FB",
	headerBackground: "rgba(255,255,255,.88)",
	utilBackground: "#071633",
	utilText: "#C9D9F8",
	utilLink: "#ffffff",
	primaryCtaText: "#ffffff",
	ghostCtaText: "#0F3D91",
	megaBackground: "#ffffff",
	regionPopBackground: "#ffffff",
	regionPopHeading: "#5B6B8C",
	regionItemText: "#0B1D3F",
	regionItemSelectedBackground: "#EDF3FF",
	regionItemSelectedText: "#0F3D91",
	navItemHoverText: "#0F3D91",
	navItemHoverBackground: "transparent",
	megaLinkHoverText: "#0F3D91",
	megaLinkHoverBackground: "#EDF3FF",
	accentSoft: "#E4EDFF",
} as const;

const CSS_VAR_NAME_RE = /^var\(\s*(--[a-z0-9-]+)(?:\s*,[^)]*)?\s*\)$/i;

function cssCustomPropertyName(value: string): string | null {
	const match = CSS_VAR_NAME_RE.exec(value.trim());
	return match?.[1]?.toLowerCase() ?? null;
}

function normalizeCssColorToken(value: string): string {
	return value.trim().toLowerCase().replace(/\s+/g, " ");
}

export function isInheritedGroupHeaderColor(
	value: string | null | undefined,
	themeToken: string,
	legacyHex?: string
): boolean {
	const trimmed = value?.trim() ?? "";
	if (!trimmed) {
		return true;
	}
	if (
		legacyHex &&
		normalizeCssColorToken(trimmed) === normalizeCssColorToken(legacyHex)
	) {
		return true;
	}
	const valueVar = cssCustomPropertyName(trimmed);
	const themeVar = cssCustomPropertyName(themeToken);
	if (valueVar && themeVar && valueVar === themeVar) {
		return true;
	}
	return normalizeCssColorToken(trimmed) === normalizeCssColorToken(themeToken);
}

export function resolveGroupHeaderColor(
	value: string | null | undefined,
	themeToken: string,
	legacyHex?: string
): string {
	if (isInheritedGroupHeaderColor(value, themeToken, legacyHex)) {
		return themeToken;
	}
	return value?.trim() ?? themeToken;
}

/** Inspector: empty picker (“Inherit default”) for theme / legacy replica hex. */
export function authoredGroupHeaderColor(
	value: string | null | undefined,
	themeToken: string,
	legacyHex?: string
): string {
	return isInheritedGroupHeaderColor(value, themeToken, legacyHex)
		? ""
		: (value?.trim() ?? "");
}

export function resolveGroupHeaderDesign(
	design: GroupHeaderDesignTokens
): GroupHeaderDesignTokens {
	const t = GROUP_HEADER_THEME_TOKEN_DEFAULTS;
	const h = LEGACY_GROUP_HEADER_HEX;
	return {
		...design,
		navy: resolveGroupHeaderColor(design.navy, t.navy, h.navy),
		royal: resolveGroupHeaderColor(design.royal, t.royal, h.royal),
		electric: resolveGroupHeaderColor(design.electric, t.electric, h.electric),
		sky: resolveGroupHeaderColor(design.sky, t.sky, h.sky),
		ice: resolveGroupHeaderColor(design.ice, t.ice, h.ice),
		ink: resolveGroupHeaderColor(design.ink, t.ink, h.ink),
		mute: resolveGroupHeaderColor(design.mute, t.mute, h.mute),
		line: resolveGroupHeaderColor(design.line, t.line, h.line),
		lineSubtle: resolveGroupHeaderColor(
			design.lineSubtle,
			t.lineSubtle,
			h.lineSubtle
		),
		headerBackground: resolveGroupHeaderColor(
			design.headerBackground,
			t.headerBackground,
			h.headerBackground
		),
		utilBackground: resolveGroupHeaderColor(
			design.utilBackground,
			t.utilBackground,
			h.utilBackground
		),
		utilText: resolveGroupHeaderColor(design.utilText, t.utilText, h.utilText),
		utilLink: resolveGroupHeaderColor(design.utilLink, t.utilLink, h.utilLink),
		primaryCtaText: resolveGroupHeaderColor(
			design.primaryCtaText,
			t.primaryCtaText,
			h.primaryCtaText
		),
		ghostCtaText: resolveGroupHeaderColor(
			design.ghostCtaText,
			t.ghostCtaText,
			h.ghostCtaText
		),
		megaBackground: resolveGroupHeaderColor(
			design.megaBackground,
			t.megaBackground,
			h.megaBackground
		),
		regionPopBackground: resolveGroupHeaderColor(
			design.regionPopBackground,
			t.regionPopBackground,
			h.regionPopBackground
		),
		regionPopHeading: resolveGroupHeaderColor(
			design.regionPopHeading,
			t.regionPopHeading,
			h.regionPopHeading
		),
		regionItemText: resolveGroupHeaderColor(
			design.regionItemText,
			t.regionItemText,
			h.regionItemText
		),
		regionItemSelectedBackground: resolveGroupHeaderColor(
			design.regionItemSelectedBackground,
			t.regionItemSelectedBackground,
			h.regionItemSelectedBackground
		),
		regionItemSelectedText: resolveGroupHeaderColor(
			design.regionItemSelectedText,
			t.regionItemSelectedText,
			h.regionItemSelectedText
		),
		navItemHoverText: resolveGroupHeaderColor(
			design.navItemHoverText,
			t.navItemHoverText,
			h.navItemHoverText
		),
		navItemHoverBackground: resolveGroupHeaderColor(
			design.navItemHoverBackground,
			t.navItemHoverBackground,
			h.navItemHoverBackground
		),
		megaLinkHoverText: resolveGroupHeaderColor(
			design.megaLinkHoverText,
			t.megaLinkHoverText,
			h.megaLinkHoverText
		),
		megaLinkHoverBackground: resolveGroupHeaderColor(
			design.megaLinkHoverBackground,
			t.megaLinkHoverBackground,
			h.megaLinkHoverBackground
		),
	};
}

/** Featured slot: structured card (default) or editor-authored HTML. */
export type GroupHeaderFeaturedContentMode = "card" | "html";

export interface GroupHeaderFeaturedCard {
	body: string;
	/**
	 * `card` (default) — eyebrow, headline, body, metric, and CTA.
	 * `html` — `html` replaces that card in the mega-panel featured slot.
	 */
	contentMode?: GroupHeaderFeaturedContentMode;
	cta: GroupHeaderCtaLink;
	enabled: boolean;
	/** Optional CSS colour for the radial mid-stop (`--ghh-feat-mid`). */
	gradientMid?: string;
	/** Markup when `contentMode` is `"html"`. Kept when switching back to card. */
	html?: string;
	label: string;
	metric?: string;
	metricCaption?: string;
	mutedTextColor?: string;
	/** Decorative rings on the featured card. Default true. */
	ringsEnabled?: boolean;
	textColor?: string;
	title: string;
}

export interface GroupHeaderProductCardLink {
	badge?: string;
	href: string;
	label: string;
}

/** Avatar in the product-card mark circle. Missing / unknown → initials. */
export type GroupHeaderProductMarkKind = "initials" | "icon";

export interface GroupHeaderProductCard {
	accent: string;
	accentDeep: string;
	ctas: GroupHeaderCtaLink[];
	/**
	 * Iconify id from `BuilderIconPickerPanel` (e.g. `lucide:calculator`).
	 * Render uses persisted `svgMarkup`, not a live Iconify fetch.
	 */
	iconId?: string;
	id: string;
	links: GroupHeaderProductCardLink[];
	logoMediaId?: string;
	logoUrl?: string;
	/** `"icon"` shows `svgMarkup` in the mark; otherwise initials (`mono`). */
	markKind?: GroupHeaderProductMarkKind;
	mono: string;
	name: string;
	subtitle: string;
	/** Sanitized inline SVG from the native `iconify_icon` picker. */
	svgMarkup?: string;
}

export interface GroupHeaderDeadline {
	href?: string;
	id: string;
	isoDate: string;
	subtitle: string;
	title: string;
}

export interface GroupHeaderDeadlinesWidget {
	enabled: boolean;
	heading: string;
	items: GroupHeaderDeadline[];
	moreHref: string;
	moreLabel: string;
	soonThresholdDays: number;
}

export interface GroupHeaderStatusPill {
	enabled: boolean;
	label: string;
	updatedLabel: string;
}

export interface GroupHeaderPanelFooter {
	ctas: GroupHeaderCtaLink[];
	enabled: boolean;
	links: GroupHeaderFooterNavLink[];
	message: string;
}

export interface GroupHeaderPanelConfig {
	deadlines?: GroupHeaderDeadlinesWidget;
	featured?: GroupHeaderFeaturedCard;
	footer?: GroupHeaderPanelFooter;
	/**
	 * Extra dense groups when `subcategoryMode` is `inline_groups`
	 * (or merged after menu groups).
	 */
	inlineGroups?: GroupHeaderDenseGroup[];
	layout: GroupHeaderMegaPanelLayout;
	products?: GroupHeaderProductCard[];
	status?: GroupHeaderStatusPill;
	subcategoryMenuId?: string | null;
	subcategoryMode: GroupHeaderSubcategoryMode;
}

export interface GroupHeaderMobileDrawer {
	accordionColumns: number;
	bodyLayout: GroupHeaderDrawerBodyLayout;
	showCtas: boolean;
	side: GroupHeaderDrawerSide;
	style: GroupHeaderMobileStyle;
	title: string;
	widthPx: number;
}

export interface GroupMegaMenuHeaderRuntimePayload {
	actions: GroupHeaderActions;
	behavior: GroupHeaderBehavior;
	breakpoints: GroupHeaderBreakpoints;
	design: GroupHeaderDesignTokens;
	/**
	 * “All …” rows keyed by mega_group menu item id.
	 */
	groupMoreLinks: Record<string, GroupHeaderGroupMoreLink>;
	layout: GroupHeaderBarLayout;
	/**
	 * Link pills keyed by mega_link (or product-link) menu item id.
	 */
	linkBadges: Record<string, GroupHeaderLinkBadge>;
	logo: GroupHeaderLogoSwitcher;
	menuId: string;
	menuName?: string;
	mobile: GroupHeaderMobileDrawer;
	/** Per mega-tab id: layout variant, featured, products, deadlines, footer. */
	panels: Record<string, GroupHeaderPanelConfig>;
	roots: MenuRootNode[];
	typography: GroupHeaderTypography;
	utility: GroupHeaderUtility;
}

/**
 * Shallow payload patch for {@link mergeGroupMegaMenuHeaderPayload}.
 * `design` may omit keys — {@link mergeGroupHeaderDesign} fills defaults.
 */
export type GroupMegaMenuHeaderPayloadPatch = Partial<
	Omit<GroupMegaMenuHeaderRuntimePayload, "design">
> & {
	design?: Partial<GroupHeaderDesignTokens> | null;
};

export const DEFAULT_GROUP_HEADER_DESIGN: GroupHeaderDesignTokens = {
	...GROUP_HEADER_THEME_TOKEN_DEFAULTS,
	radiusMd: "14px",
	radiusLg: "22px",
	maxWidthPx: 1400,
	wrapPaddingPx: 28,
};

export const DEFAULT_GROUP_HEADER_TYPOGRAPHY: GroupHeaderTypography = {
	fontFamily: '"Manrope",system-ui,-apple-system,"Segoe UI",sans-serif',
	navFontSizePx: 14.5,
	navFontWeight: 600,
	groupTitleFontSizePx: 13,
	linkFontSizePx: 13.5,
	utilFontSizePx: 12.5,
};

export const DEFAULT_GROUP_HEADER_BREAKPOINTS: GroupHeaderBreakpoints = {
	megaAsideHidePx: 1240,
	drawerPx: 1024,
	compactCtaHidePx: 560,
};

export const DEFAULT_GROUP_HEADER_BEHAVIOR: GroupHeaderBehavior = {
	sticky: true,
	stickyMode: "compact",
	compactScrollY: 24,
	barHeightPx: 76,
	compactBarHeightPx: 64,
	utilHeightPx: 38,
	zIndex: 100,
	glassBackground: true,
	glassBlurPx: 18,
	barShadow: true,
	openDelayMs: 120,
	closeDelayMs: 180,
	navIconPosition: "left",
	showNavCounts: true,
};

const DEFAULT_DISABLED_ACTION: GroupHeaderCtaLink = {
	enabled: false,
	href: "#",
	label: "",
};

export const DEFAULT_GROUP_HEADER_SEARCH: GroupHeaderSearch = {
	enabled: true,
	style: "modal",
	placeholder: "Search…",
	shortcutEnabled: true,
	emptyMessage: "Nothing matches. Try another term.",
	maxResults: 10,
};

export const DEFAULT_GROUP_HEADER_MOBILE: GroupHeaderMobileDrawer = {
	style: "drawer",
	side: "right",
	bodyLayout: "accordion_columns",
	widthPx: 440,
	title: "Menu",
	showCtas: true,
	accordionColumns: 2,
};

function pickNonEmptyString(value: unknown, fallback: string): string {
	return typeof value === "string" && value.trim() ? value : fallback;
}

/** Empty string stays empty (inspector inherit). Missing key uses fallback. */
function pickColorToken(value: unknown, fallback: string): string {
	if (typeof value !== "string") {
		return fallback;
	}
	return value.trim();
}

function pickString(value: unknown, fallback: string): string {
	return typeof value === "string" ? value : fallback;
}

function pickFiniteNumber(value: unknown, fallback: number): number {
	return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function pickBoolean(value: unknown, fallback: boolean): boolean {
	return typeof value === "boolean" ? value : fallback;
}

export function mergeGroupHeaderDesign(
	design: Partial<GroupHeaderDesignTokens> | null | undefined
): GroupHeaderDesignTokens {
	return {
		navy: pickColorToken(design?.navy, DEFAULT_GROUP_HEADER_DESIGN.navy),
		royal: pickColorToken(design?.royal, DEFAULT_GROUP_HEADER_DESIGN.royal),
		electric: pickColorToken(
			design?.electric,
			DEFAULT_GROUP_HEADER_DESIGN.electric
		),
		sky: pickColorToken(design?.sky, DEFAULT_GROUP_HEADER_DESIGN.sky),
		ice: pickColorToken(design?.ice, DEFAULT_GROUP_HEADER_DESIGN.ice),
		ink: pickColorToken(design?.ink, DEFAULT_GROUP_HEADER_DESIGN.ink),
		mute: pickColorToken(design?.mute, DEFAULT_GROUP_HEADER_DESIGN.mute),
		line: pickColorToken(design?.line, DEFAULT_GROUP_HEADER_DESIGN.line),
		lineSubtle: pickColorToken(
			design?.lineSubtle,
			DEFAULT_GROUP_HEADER_DESIGN.lineSubtle
		),
		headerBackground: pickColorToken(
			design?.headerBackground,
			DEFAULT_GROUP_HEADER_DESIGN.headerBackground
		),
		utilBackground: pickColorToken(
			design?.utilBackground,
			DEFAULT_GROUP_HEADER_DESIGN.utilBackground
		),
		utilText: pickColorToken(
			design?.utilText,
			DEFAULT_GROUP_HEADER_DESIGN.utilText
		),
		utilLink: pickColorToken(
			design?.utilLink,
			DEFAULT_GROUP_HEADER_DESIGN.utilLink
		),
		primaryCtaText: pickColorToken(
			design?.primaryCtaText,
			DEFAULT_GROUP_HEADER_DESIGN.primaryCtaText
		),
		ghostCtaText: pickColorToken(
			design?.ghostCtaText,
			DEFAULT_GROUP_HEADER_DESIGN.ghostCtaText
		),
		megaBackground: pickColorToken(
			design?.megaBackground,
			DEFAULT_GROUP_HEADER_DESIGN.megaBackground
		),
		regionPopBackground: pickColorToken(
			design?.regionPopBackground,
			DEFAULT_GROUP_HEADER_DESIGN.regionPopBackground
		),
		regionPopHeading: pickColorToken(
			design?.regionPopHeading,
			DEFAULT_GROUP_HEADER_DESIGN.regionPopHeading
		),
		regionItemText: pickColorToken(
			design?.regionItemText,
			DEFAULT_GROUP_HEADER_DESIGN.regionItemText
		),
		regionItemSelectedBackground: pickColorToken(
			design?.regionItemSelectedBackground,
			DEFAULT_GROUP_HEADER_DESIGN.regionItemSelectedBackground
		),
		regionItemSelectedText: pickColorToken(
			design?.regionItemSelectedText,
			DEFAULT_GROUP_HEADER_DESIGN.regionItemSelectedText
		),
		navItemHoverText: pickColorToken(
			design?.navItemHoverText,
			DEFAULT_GROUP_HEADER_DESIGN.navItemHoverText
		),
		navItemHoverBackground: pickColorToken(
			design?.navItemHoverBackground,
			DEFAULT_GROUP_HEADER_DESIGN.navItemHoverBackground
		),
		megaLinkHoverText: pickColorToken(
			design?.megaLinkHoverText,
			DEFAULT_GROUP_HEADER_DESIGN.megaLinkHoverText
		),
		megaLinkHoverBackground: pickColorToken(
			design?.megaLinkHoverBackground,
			DEFAULT_GROUP_HEADER_DESIGN.megaLinkHoverBackground
		),
		radiusMd: pickNonEmptyString(
			design?.radiusMd,
			DEFAULT_GROUP_HEADER_DESIGN.radiusMd
		),
		radiusLg: pickNonEmptyString(
			design?.radiusLg,
			DEFAULT_GROUP_HEADER_DESIGN.radiusLg
		),
		maxWidthPx: pickFiniteNumber(
			design?.maxWidthPx,
			DEFAULT_GROUP_HEADER_DESIGN.maxWidthPx
		),
		wrapPaddingPx: pickFiniteNumber(
			design?.wrapPaddingPx,
			DEFAULT_GROUP_HEADER_DESIGN.wrapPaddingPx
		),
	};
}

export function mergeGroupHeaderTypography(
	typography: Partial<GroupHeaderTypography> | null | undefined
): GroupHeaderTypography {
	return {
		fontFamily: pickNonEmptyString(
			typography?.fontFamily,
			DEFAULT_GROUP_HEADER_TYPOGRAPHY.fontFamily
		),
		navFontSizePx: pickFiniteNumber(
			typography?.navFontSizePx,
			DEFAULT_GROUP_HEADER_TYPOGRAPHY.navFontSizePx
		),
		navFontWeight: pickFiniteNumber(
			typography?.navFontWeight,
			DEFAULT_GROUP_HEADER_TYPOGRAPHY.navFontWeight
		),
		groupTitleFontSizePx: pickFiniteNumber(
			typography?.groupTitleFontSizePx,
			DEFAULT_GROUP_HEADER_TYPOGRAPHY.groupTitleFontSizePx
		),
		linkFontSizePx: pickFiniteNumber(
			typography?.linkFontSizePx,
			DEFAULT_GROUP_HEADER_TYPOGRAPHY.linkFontSizePx
		),
		utilFontSizePx: pickFiniteNumber(
			typography?.utilFontSizePx,
			DEFAULT_GROUP_HEADER_TYPOGRAPHY.utilFontSizePx
		),
	};
}

export function mergeGroupHeaderBreakpoints(
	breakpoints: Partial<GroupHeaderBreakpoints> | null | undefined
): GroupHeaderBreakpoints {
	return {
		megaAsideHidePx: pickFiniteNumber(
			breakpoints?.megaAsideHidePx,
			DEFAULT_GROUP_HEADER_BREAKPOINTS.megaAsideHidePx
		),
		drawerPx: pickFiniteNumber(
			breakpoints?.drawerPx,
			DEFAULT_GROUP_HEADER_BREAKPOINTS.drawerPx
		),
		compactCtaHidePx: pickFiniteNumber(
			breakpoints?.compactCtaHidePx,
			DEFAULT_GROUP_HEADER_BREAKPOINTS.compactCtaHidePx
		),
	};
}

export function mergeGroupHeaderBehavior(
	behavior: Partial<GroupHeaderBehavior> | null | undefined
): GroupHeaderBehavior {
	const stickyMode =
		behavior?.stickyMode === "always" ||
		behavior?.stickyMode === "smart" ||
		behavior?.stickyMode === "compact"
			? behavior.stickyMode
			: DEFAULT_GROUP_HEADER_BEHAVIOR.stickyMode;
	return {
		sticky: pickBoolean(behavior?.sticky, DEFAULT_GROUP_HEADER_BEHAVIOR.sticky),
		stickyMode,
		compactScrollY: pickFiniteNumber(
			behavior?.compactScrollY,
			DEFAULT_GROUP_HEADER_BEHAVIOR.compactScrollY
		),
		barHeightPx: pickFiniteNumber(
			behavior?.barHeightPx,
			DEFAULT_GROUP_HEADER_BEHAVIOR.barHeightPx
		),
		compactBarHeightPx: pickFiniteNumber(
			behavior?.compactBarHeightPx,
			DEFAULT_GROUP_HEADER_BEHAVIOR.compactBarHeightPx
		),
		utilHeightPx: pickFiniteNumber(
			behavior?.utilHeightPx,
			DEFAULT_GROUP_HEADER_BEHAVIOR.utilHeightPx
		),
		zIndex: pickFiniteNumber(
			behavior?.zIndex,
			DEFAULT_GROUP_HEADER_BEHAVIOR.zIndex
		),
		glassBackground: pickBoolean(
			behavior?.glassBackground,
			DEFAULT_GROUP_HEADER_BEHAVIOR.glassBackground
		),
		glassBlurPx: pickFiniteNumber(
			behavior?.glassBlurPx,
			DEFAULT_GROUP_HEADER_BEHAVIOR.glassBlurPx
		),
		barShadow: pickBoolean(
			behavior?.barShadow,
			DEFAULT_GROUP_HEADER_BEHAVIOR.barShadow
		),
		openDelayMs: pickFiniteNumber(
			behavior?.openDelayMs,
			DEFAULT_GROUP_HEADER_BEHAVIOR.openDelayMs
		),
		closeDelayMs: pickFiniteNumber(
			behavior?.closeDelayMs,
			DEFAULT_GROUP_HEADER_BEHAVIOR.closeDelayMs
		),
		showNavCounts: pickBoolean(
			behavior?.showNavCounts,
			DEFAULT_GROUP_HEADER_BEHAVIOR.showNavCounts
		),
		navIconPosition:
			behavior?.navIconPosition === "right"
				? "right"
				: DEFAULT_GROUP_HEADER_BEHAVIOR.navIconPosition,
	};
}

function mergeActionLink(
	link: GroupHeaderCtaLink | null | undefined,
	fallback: GroupHeaderCtaLink
): GroupHeaderCtaLink {
	if (!link) {
		return fallback;
	}
	return {
		enabled: pickBoolean(link.enabled, fallback.enabled),
		label: pickString(link.label, fallback.label),
		href: pickString(link.href, fallback.href),
	};
}

export function mergeGroupHeaderSearch(
	search: Partial<GroupHeaderSearch> | null | undefined
): GroupHeaderSearch {
	const style =
		search?.style === "icon" ||
		search?.style === "bar" ||
		search?.style === "modal"
			? search.style
			: DEFAULT_GROUP_HEADER_SEARCH.style;
	return {
		enabled: pickBoolean(search?.enabled, DEFAULT_GROUP_HEADER_SEARCH.enabled),
		style,
		placeholder: pickNonEmptyString(
			search?.placeholder,
			DEFAULT_GROUP_HEADER_SEARCH.placeholder
		),
		shortcutEnabled: pickBoolean(
			search?.shortcutEnabled,
			DEFAULT_GROUP_HEADER_SEARCH.shortcutEnabled
		),
		emptyMessage: pickNonEmptyString(
			search?.emptyMessage,
			DEFAULT_GROUP_HEADER_SEARCH.emptyMessage
		),
		maxResults: pickFiniteNumber(
			search?.maxResults,
			DEFAULT_GROUP_HEADER_SEARCH.maxResults
		),
	};
}

export function mergeGroupHeaderMobile(
	mobile: Partial<GroupHeaderMobileDrawer> | null | undefined
): GroupHeaderMobileDrawer {
	const style =
		mobile?.style === "hamburger" ||
		mobile?.style === "drawer" ||
		mobile?.style === "bottom_sheet"
			? mobile.style
			: DEFAULT_GROUP_HEADER_MOBILE.style;
	const side =
		mobile?.side === "left" ? "left" : DEFAULT_GROUP_HEADER_MOBILE.side;
	const bodyLayout =
		mobile?.bodyLayout === "list" ||
		mobile?.bodyLayout === "accordion" ||
		mobile?.bodyLayout === "accordion_columns"
			? mobile.bodyLayout
			: DEFAULT_GROUP_HEADER_MOBILE.bodyLayout;
	return {
		style,
		side,
		bodyLayout,
		widthPx: pickFiniteNumber(
			mobile?.widthPx,
			DEFAULT_GROUP_HEADER_MOBILE.widthPx
		),
		title: pickNonEmptyString(mobile?.title, DEFAULT_GROUP_HEADER_MOBILE.title),
		showCtas: pickBoolean(
			mobile?.showCtas,
			DEFAULT_GROUP_HEADER_MOBILE.showCtas
		),
		accordionColumns: pickFiniteNumber(
			mobile?.accordionColumns,
			DEFAULT_GROUP_HEADER_MOBILE.accordionColumns
		),
	};
}

const EMPTY_PANEL_FOOTER: GroupHeaderPanelFooter = {
	enabled: true,
	message: "",
	links: [],
	ctas: [],
};

const EMPTY_FEATURED_CTA: GroupHeaderCtaLink = {
	enabled: true,
	href: "#",
	label: "Learn more",
};

function mergeFeaturedContentMode(
	value: unknown
): GroupHeaderFeaturedContentMode {
	return value === "html" ? "html" : "card";
}

function mergeFeaturedCard(
	card: GroupHeaderFeaturedCard | null | undefined
): GroupHeaderFeaturedCard | undefined {
	if (!card) {
		return undefined;
	}
	return {
		enabled: pickBoolean(card.enabled, false),
		contentMode: mergeFeaturedContentMode(card.contentMode),
		label: pickString(card.label, ""),
		title: pickString(card.title, ""),
		body: pickString(card.body, ""),
		html: typeof card.html === "string" ? card.html : undefined,
		cta: mergeActionLink(card.cta, EMPTY_FEATURED_CTA),
		metric: typeof card.metric === "string" ? card.metric : undefined,
		metricCaption:
			typeof card.metricCaption === "string" ? card.metricCaption : undefined,
		gradientMid:
			typeof card.gradientMid === "string" ? card.gradientMid : undefined,
		textColor: typeof card.textColor === "string" ? card.textColor : undefined,
		mutedTextColor:
			typeof card.mutedTextColor === "string" ? card.mutedTextColor : undefined,
		ringsEnabled: pickBoolean(card.ringsEnabled, true),
	};
}

export function parseGroupHeaderProductMarkKind(
	value: unknown
): GroupHeaderProductMarkKind {
	return value === "icon" ? "icon" : "initials";
}

function uniquifyProductCardIds(
	cards: GroupHeaderProductCard[]
): GroupHeaderProductCard[] {
	const seen = new Set<string>();
	return cards.map((card, index) => {
		let id = card.id;
		if (!id || seen.has(id)) {
			id = `${id || "product"}-${String(index)}`;
		}
		seen.add(id);
		return id === card.id ? card : { ...card, id };
	});
}

function mergeProductCard(
	card: GroupHeaderProductCard,
	index = 0
): GroupHeaderProductCard {
	return {
		id: pickNonEmptyString(card.id, `product-${String(index)}`),
		mono: pickString(card.mono, ""),
		name: pickString(card.name, ""),
		subtitle: pickString(card.subtitle, ""),
		accent: pickColorToken(
			card.accent,
			GROUP_HEADER_THEME_TOKEN_DEFAULTS.electric
		),
		accentDeep: pickColorToken(
			card.accentDeep,
			GROUP_HEADER_THEME_TOKEN_DEFAULTS.royal
		),
		markKind: parseGroupHeaderProductMarkKind(card.markKind),
		iconId: typeof card.iconId === "string" ? card.iconId : undefined,
		svgMarkup: typeof card.svgMarkup === "string" ? card.svgMarkup : undefined,
		logoUrl: typeof card.logoUrl === "string" ? card.logoUrl : undefined,
		logoMediaId:
			typeof card.logoMediaId === "string" ? card.logoMediaId : undefined,
		links: Array.isArray(card.links)
			? card.links.map((link) => ({
					label: pickString(link.label, ""),
					href: pickString(link.href, "#"),
					badge: typeof link.badge === "string" ? link.badge : undefined,
				}))
			: [],
		ctas: Array.isArray(card.ctas)
			? card.ctas.map((cta) =>
					mergeActionLink(cta, { enabled: false, href: "#", label: "" })
				)
			: [],
	};
}

function mergeDeadlinesWidget(
	widget: GroupHeaderDeadlinesWidget | null | undefined
): GroupHeaderDeadlinesWidget | undefined {
	if (!widget) {
		return undefined;
	}
	return {
		enabled: pickBoolean(widget.enabled, false),
		heading: pickNonEmptyString(widget.heading, "Upcoming dates"),
		items: Array.isArray(widget.items)
			? widget.items.map((item) => ({
					id: pickNonEmptyString(item.id, "deadline"),
					isoDate: pickString(item.isoDate, ""),
					title: pickString(item.title, ""),
					subtitle: pickString(item.subtitle, ""),
					href: typeof item.href === "string" ? item.href : undefined,
				}))
			: [],
		moreHref: pickString(widget.moreHref, "#"),
		moreLabel: pickNonEmptyString(widget.moreLabel, "Full calendar"),
		soonThresholdDays: pickFiniteNumber(widget.soonThresholdDays, 45),
	};
}

function mergeStatusPill(
	status: GroupHeaderStatusPill | null | undefined
): GroupHeaderStatusPill | undefined {
	if (!status) {
		return undefined;
	}
	return {
		enabled: pickBoolean(status.enabled, false),
		label: pickString(status.label, "All systems operational"),
		updatedLabel: pickString(status.updatedLabel, ""),
	};
}

function mergePanelFooter(
	footer: GroupHeaderPanelFooter | null | undefined
): GroupHeaderPanelFooter {
	if (!footer) {
		return EMPTY_PANEL_FOOTER;
	}
	return {
		enabled: pickBoolean(footer.enabled, true),
		message: pickString(footer.message, ""),
		links: Array.isArray(footer.links) ? footer.links : [],
		ctas: Array.isArray(footer.ctas)
			? footer.ctas.map((cta) =>
					mergeActionLink(cta, { enabled: false, href: "#", label: "" })
				)
			: [],
	};
}

/**
 * Normalises one per-tab mega panel. Layout aliases from older region dumps
 * (`services` / `software` / …) collapse onto this single schema.
 */
export function mergeGroupHeaderPanelConfig(
	panel: Partial<GroupHeaderPanelConfig> | null | undefined
): GroupHeaderPanelConfig {
	const layout = parseGroupHeaderMegaPanelLayout(panel?.layout);
	const subcategoryMode: GroupHeaderSubcategoryMode =
		panel?.subcategoryMode === "linked_menu" ||
		panel?.subcategoryMode === "inline_groups" ||
		panel?.subcategoryMode === "menu_groups"
			? panel.subcategoryMode
			: "menu_groups";
	return {
		layout,
		subcategoryMode,
		subcategoryMenuId:
			typeof panel?.subcategoryMenuId === "string"
				? panel.subcategoryMenuId
				: (panel?.subcategoryMenuId ?? null),
		inlineGroups: Array.isArray(panel?.inlineGroups)
			? panel.inlineGroups
			: undefined,
		featured: mergeFeaturedCard(panel?.featured),
		products: Array.isArray(panel?.products)
			? uniquifyProductCardIds(
					panel.products.map((card, index) => mergeProductCard(card, index))
				)
			: undefined,
		deadlines: mergeDeadlinesWidget(panel?.deadlines),
		status: mergeStatusPill(panel?.status),
		footer: mergePanelFooter(panel?.footer),
	};
}

export function mergeGroupHeaderPanels(
	panels: Record<string, Partial<GroupHeaderPanelConfig>> | null | undefined
): Record<string, GroupHeaderPanelConfig> {
	const out: Record<string, GroupHeaderPanelConfig> = {};
	for (const [tabId, panel] of Object.entries(panels ?? {})) {
		out[tabId] = mergeGroupHeaderPanelConfig(panel);
	}
	return out;
}

const DEFAULT_PRIMARY_CTA: GroupHeaderCtaLink = {
	enabled: true,
	label: "Book a consultation",
	href: "#",
};

const DEFAULT_SECONDARY_CTA: GroupHeaderCtaLink = {
	enabled: true,
	label: "Client login",
	href: "#",
};

/**
 * Fills missing replica-header JSON so older / partial payloads still render.
 */
export function mergeGroupMegaMenuHeaderPayload(
	payload: GroupMegaMenuHeaderPayloadPatch | null | undefined
): GroupMegaMenuHeaderRuntimePayload {
	const layout: GroupHeaderBarLayout =
		payload?.layout === "logo_actions_nav" ||
		payload?.layout === "centered_nav" ||
		payload?.layout === "stacked" ||
		payload?.layout === "logo_nav_actions"
			? payload.layout
			: "logo_nav_actions";
	return {
		menuId: pickNonEmptyString(payload?.menuId, ""),
		menuName: payload?.menuName,
		roots: payload?.roots ?? [],
		layout,
		behavior: mergeGroupHeaderBehavior(payload?.behavior),
		breakpoints: mergeGroupHeaderBreakpoints(payload?.breakpoints),
		typography: mergeGroupHeaderTypography(payload?.typography),
		design: mergeGroupHeaderDesign(payload?.design),
		logo: {
			enabled: pickBoolean(payload?.logo?.enabled, false),
			autoplayMs: pickFiniteNumber(payload?.logo?.autoplayMs, 4200),
			pauseOnHover: pickBoolean(payload?.logo?.pauseOnHover, true),
			showNavButtons: pickBoolean(payload?.logo?.showNavButtons, true),
			showDots: pickBoolean(payload?.logo?.showDots, true),
			stageWidthPx: pickFiniteNumber(payload?.logo?.stageWidthPx, 236),
			stageHeightPx: pickFiniteNumber(payload?.logo?.stageHeightPx, 48),
			brands: payload?.logo?.brands ?? [],
		},
		utility: {
			enabled: pickBoolean(payload?.utility?.enabled, false),
			ticker: {
				enabled: pickBoolean(payload?.utility?.ticker.enabled, false),
				text: pickNonEmptyString(payload?.utility?.ticker.text, ""),
				href: pickNonEmptyString(payload?.utility?.ticker.href, "#"),
				linkLabel: pickNonEmptyString(payload?.utility?.ticker.linkLabel, ""),
			},
			links: payload?.utility?.links ?? [],
			regionSwitcher: {
				enabled: pickBoolean(payload?.utility?.regionSwitcher.enabled, false),
				regions: payload?.utility?.regionSwitcher.regions ?? [],
				defaultRegionCode: pickNonEmptyString(
					payload?.utility?.regionSwitcher.defaultRegionCode,
					""
				),
				defaultLangIndex: pickFiniteNumber(
					payload?.utility?.regionSwitcher.defaultLangIndex,
					0
				),
				currencyLabel: pickNonEmptyString(
					payload?.utility?.regionSwitcher.currencyLabel,
					""
				),
			},
		},
		actions: {
			search: mergeGroupHeaderSearch(payload?.actions?.search),
			secondaryCta: mergeActionLink(
				payload?.actions?.secondaryCta,
				DEFAULT_SECONDARY_CTA
			),
			primaryCta: mergeActionLink(
				payload?.actions?.primaryCta,
				DEFAULT_PRIMARY_CTA
			),
			brandOverridesCta: pickBoolean(payload?.actions?.brandOverridesCta, true),
		},
		mobile: mergeGroupHeaderMobile(payload?.mobile),
		panels: mergeGroupHeaderPanels(payload?.panels),
		linkBadges: payload?.linkBadges ?? {},
		groupMoreLinks: payload?.groupMoreLinks ?? {},
	};
}

export const EMPTY_GROUP_MEGA_MENU_HEADER_PAYLOAD: GroupMegaMenuHeaderRuntimePayload =
	mergeGroupMegaMenuHeaderPayload({
		actions: {
			search: DEFAULT_GROUP_HEADER_SEARCH,
			secondaryCta: DEFAULT_DISABLED_ACTION,
			primaryCta: DEFAULT_DISABLED_ACTION,
			brandOverridesCta: false,
		},
	});
